import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'

class AssetLoader {
    constructor() {
        if (AssetLoader.instance) {
            return AssetLoader.instance
        }

        // Initialize loaders
        this.textureLoader = new THREE.TextureLoader()
        this.gltfLoader = new GLTFLoader()
        this.audioLoader = new THREE.AudioLoader()
        this.fileLoader = new THREE.FileLoader()
        
        // Setup DRACO loader for compressed models
        this.dracoLoader = new DRACOLoader()
        this.dracoLoader.setDecoderPath('/draco/')
        this.gltfLoader.setDRACOLoader(this.dracoLoader)

        // Storage for loaded assets
        this.loadedAssets = new Map()
        this.loadingPromises = new Map()
        this.fileLoadingStates = new Map()

        AssetLoader.instance = this
        return this
    }

    static getInstance() {
        if (!AssetLoader.instance) {
            AssetLoader.instance = new AssetLoader()
        }
        return AssetLoader.instance
    }

    /**
     * Load assets from a JSON file
     * @param {string} filePath - Path to the JSON file containing asset definitions
     * @param {function} onComplete - Callback function called when all assets are loaded
     * @param {function} onProgress - Optional progress callback
     */    async loadAssetsFromFile(filePath, onComplete = () => {}, onProgress = () => {}) {
        try {
            // Check if this file is already being processed
            if (this.fileLoadingStates.has(filePath)) {
                const state = this.fileLoadingStates.get(filePath)
                
                if (state.status === 'completed') {
                    // File already loaded, call callback immediately
                    console.log(`Asset file ${filePath} already loaded, using cached version`)
                    onComplete(state.loadedAssets)
                    return
                } else if (state.status === 'loading') {
                    // File is currently loading, add callback to queue
                    console.log(`Asset file ${filePath} is already loading, adding to queue`)
                    state.callbacks.push(onComplete)
                    return
                }
            }

            console.log(`Loading asset file: ${filePath}`)

            // Mark file as loading
            this.fileLoadingStates.set(filePath, {
                status: 'loading',
                callbacks: [onComplete],
                loadedAssets: {}
            })

            // Load the JSON file
            const response = await fetch(filePath)
            
            if (!response.ok) {
                throw new Error(`Failed to load asset file: ${filePath} (${response.status}: ${response.statusText})`)
            }

            const assetsData = await response.json()
            
            if (!assetsData || typeof assetsData !== 'object') {
                throw new Error(`Invalid asset file format: ${filePath}`)
            }
            const assetPromises = []
            const fileState = this.fileLoadingStates.get(filePath)

            // Process each asset in the JSON file
            for (const [assetKey, assetInfo] of Object.entries(assetsData)) {
                // Check if asset is already loaded
                if (this.loadedAssets.has(assetKey)) {
                    fileState.loadedAssets[assetKey] = this.loadedAssets.get(assetKey)
                    continue
                }

                // Check if asset is currently being loaded
                if (this.loadingPromises.has(assetKey)) {
                    assetPromises.push(
                        this.loadingPromises.get(assetKey).then(asset => {
                            fileState.loadedAssets[assetKey] = asset
                            return asset
                        })
                    )
                    continue
                }

                // Load new asset
                const loadPromise = this.loadSingleAsset(assetKey, assetInfo)
                this.loadingPromises.set(assetKey, loadPromise)
                
                assetPromises.push(
                    loadPromise.then(asset => {
                        fileState.loadedAssets[assetKey] = asset
                        this.loadedAssets.set(assetKey, asset)
                        this.loadingPromises.delete(assetKey)
                        return asset
                    }).catch(error => {
                        console.error(`Failed to load asset ${assetKey}:`, error)
                        this.loadingPromises.delete(assetKey)
                        return null
                    })
                )
            }

            // Wait for all assets to load
            await Promise.allSettled(assetPromises)

            // Mark file as completed and call all callbacks
            fileState.status = 'completed'
            fileState.callbacks.forEach(callback => callback(fileState.loadedAssets))

            // Report progress
            const totalAssets = Object.keys(assetsData).length
            const loadedCount = Object.keys(fileState.loadedAssets).length
            onProgress(loadedCount / totalAssets)

        } catch (error) {
            console.error('Error loading assets from file:', error)
            
            // Mark as error and call callbacks with empty object
            if (this.fileLoadingStates.has(filePath)) {
                const state = this.fileLoadingStates.get(filePath)
                state.status = 'error'
                state.callbacks.forEach(callback => callback({}))
            }
        }
    }

    /**
     * Load a single asset based on its type
     * @param {string} key - Asset key/name
     * @param {object} assetInfo - Asset information object
     */
    async loadSingleAsset(key, assetInfo) {
        const { type, path, options = {} } = assetInfo

        return new Promise((resolve, reject) => {
            switch (type) {
                case 'texture':
                    this.textureLoader.load(
                        path,
                        (texture) => {
                            // Apply texture options
                            if (options.wrapS) texture.wrapS = THREE[options.wrapS]
                            if (options.wrapT) texture.wrapT = THREE[options.wrapT]
                            if (options.magFilter) texture.magFilter = THREE[options.magFilter]
                            if (options.minFilter) texture.minFilter = THREE[options.minFilter]
                            if (options.flipY !== undefined) texture.flipY = options.flipY
                            
                            resolve(texture)
                        },
                        undefined,
                        reject
                    )
                    break

                case 'gltf':
                case 'glb':
                    this.gltfLoader.load(
                        path,
                        (gltf) => {
                            // Store the entire gltf object but also provide easy access to scene
                            const asset = {
                                scene: gltf.scene,
                                animations: gltf.animations,
                                cameras: gltf.cameras,
                                asset: gltf.asset,
                                parser: gltf.parser,
                                userData: gltf.userData
                            }
                            resolve(asset)
                        },
                        undefined,
                        reject
                    )
                    break

                case 'audio':
                    this.audioLoader.load(
                        path,
                        resolve,
                        undefined,
                        reject
                    )
                    break

                case 'json':
                    this.fileLoader.load(
                        path,
                        (data) => {
                            try {
                                const parsed = JSON.parse(data)
                                resolve(parsed)
                            } catch (error) {
                                reject(error)
                            }
                        },
                        undefined,
                        reject
                    )
                    break

                case 'text':
                    this.fileLoader.load(
                        path,
                        resolve,
                        undefined,
                        reject
                    )
                    break

                default:
                    reject(new Error(`Unknown asset type: ${type}`))
            }
        })
    }

    /**
     * Get a loaded asset by key
     * @param {string} key - Asset key
     * @returns {any} The loaded asset or null if not found
     */
    getAsset(key) {
        return this.loadedAssets.get(key) || null
    }

    /**
     * Check if an asset is loaded
     * @param {string} key - Asset key
     * @returns {boolean}
     */
    hasAsset(key) {
        return this.loadedAssets.has(key)
    }

    /**
     * Get all loaded assets
     * @returns {Map} Map of all loaded assets
     */
    getAllAssets() {
        return new Map(this.loadedAssets)
    }

    /**
     * Clear all loaded assets (useful for memory management)
     */
    clearAssets() {
        this.loadedAssets.clear()
        this.loadingPromises.clear()
        this.fileLoadingStates.clear()
    }

    /**
     * Remove a specific asset
     * @param {string} key - Asset key to remove
     */
    removeAsset(key) {
        this.loadedAssets.delete(key)
    }

    /**
     * Get loading progress for all currently loading assets
     * @returns {object} Progress information
     */
    getLoadingProgress() {
        const totalFiles = this.fileLoadingStates.size
        const completedFiles = Array.from(this.fileLoadingStates.values())
            .filter(state => state.status === 'completed').length
        
        return {
            filesTotal: totalFiles,
            filesLoaded: completedFiles,
            filesProgress: totalFiles > 0 ? completedFiles / totalFiles : 1,
            assetsTotal: this.loadedAssets.size + this.loadingPromises.size,
            assetsLoaded: this.loadedAssets.size
        }
    }
}

export default AssetLoader
